import { useQuery } from "@tanstack/react-query";

import { getWordProgressBySet } from "@/api/wordProgress";
import { getWordsBySetId } from "@/api/words";
import { useAuthStore } from "@/store/authStore";
import type { FlashcardWord } from "@/types/common";

export type FlashcardWordsData = {
  /** 필터링된 학습 대상 단어 */
  words: FlashcardWord[];
  /** 세트 전체 단어 (리셋용) */
  allWords: FlashcardWord[];
  /** 전체 단어 수 (필터링 전) */
  totalCount: number;
  /** DB에 저장된 known 단어 수 */
  knownCount: number;
  /** DB에 저장된 learning 단어 수 */
  learningCount: number;
  /**
   * 미분류가 없고(세트 단어는 전부 known/learning으로 분류됨) 학습 중 단어만 큐인 경우.
   * 헤더는 이 모드에서 `진행 카드 / 덱 크기(학습 중)` 형태로 표시한다.
   */
  isLearningReviewDeck: boolean;
};

/** 세트별 플래시카드 단어 + 진행 상태 통합 조회 */
export function useFlashcardWordsQuery(setId: string | undefined) {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: ["flashcardWords", setId, userId],
    queryFn: async (): Promise<FlashcardWordsData> => {
      const emptyProgress = { knownWordIds: new Set<string>(), learningWordIds: new Set<string>() };

      const [rawWords, progress] = await Promise.all([
        getWordsBySetId(setId!),
        userId ? getWordProgressBySet(userId, setId!) : emptyProgress,
      ]);

      const toFlashcard = (w: { id: string; word: string; meaning: string }): FlashcardWord => ({
        id: w.id,
        word: w.word,
        meaning: w.meaning,
      });

      const unclassified = rawWords.filter(
        w => !progress.knownWordIds.has(w.id) && !progress.learningWordIds.has(w.id),
      );

      // 미분류 단어가 있으면 미분류 단어를, 모두 분류됐으면 학습 중 단어를 표시
      const words =
        unclassified.length > 0
          ? unclassified.map(toFlashcard)
          : rawWords.filter(w => progress.learningWordIds.has(w.id)).map(toFlashcard);

      return {
        words,
        allWords: rawWords.map(toFlashcard),
        totalCount: rawWords.length,
        knownCount: progress.knownWordIds.size,
        learningCount: progress.learningWordIds.size,
        isLearningReviewDeck: unclassified.length === 0 && rawWords.length > 0,
      };
    },
    enabled: Boolean(setId) && Boolean(userId) && setId !== "favorites",
  });
}
